import { useCallback, useEffect, useState } from "react";

import { downloadBlob } from "@/shared/utils/downloadBlob";
import { type ImageDelivery, shareImage } from "@/shared/utils/shareImage";

import { type PayinQrImageModel, renderPayinQrImage } from "../utils/renderPayinQrImage";

interface RenderedQr {
  model: PayinQrImageModel;
  image: Blob | null;
}

// Drawn before the click: Safari opens the share sheet only within a user gesture, which drawing would spend.
export function usePayinQrShare(model: PayinQrImageModel | null, fileName: string) {
  const [rendered, setRendered] = useState<RenderedQr | null>(null);

  useEffect(() => {
    if (!model) return;
    let cancelled = false;
    renderPayinQrImage(model)
      .then((image) => {
        if (!cancelled) setRendered({ model, image });
      })
      .catch(() => {
        if (!cancelled) setRendered({ model, image: null });
      });
    return () => {
      cancelled = true;
    };
  }, [model]);

  const image = model && rendered?.model === model ? rendered.image : null;

  const share = useCallback(async (): Promise<ImageDelivery | null> => {
    if (!model || !image) return null;
    const text = model.amount ? `${model.title} · ${model.amount}` : model.title;
    return shareImage(image, { fileName, title: model.title, text });
  }, [fileName, image, model]);

  const download = useCallback(() => {
    if (image) downloadBlob(image, fileName);
  }, [fileName, image]);

  return { ready: image !== null, share, download };
}
